import * as React from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import * as CSIDActions from "../../store/csid/actions";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";
import * as services from "../../store/service";
import { Notification } from "../common/Notification";
import { ApplicationState } from "../../store";

interface IProps {
    onCloseCallback?: any;
}

const ProfileCompleteDialog: React.FunctionComponent<IProps> = (props) => {
    const ssn = "SSN";
    const ssnFullText = "Social Security Number";

    const dispatch = useDispatch();
    const getUserCSIDStatus = (forceRefresh: boolean) => dispatch(CSIDActions.actionCreators.getUserCSIDStatus(forceRefresh));
    const csidUserStatus = useSelector((state: ApplicationState) =>
        state.csidUserStatusState && state.csidUserStatusState.csidUserStatus ? state.csidUserStatusState.csidUserStatus : null
    );

    const { t: tLang, i18n: i18nLang } = useTranslation();

    const initState = {
        "check-ssn-3-digit": "",
        "check-ssn-2-digit": "",
        "check-ssn-4-digit": "",
        "confirm-ssn-3-digit": "",
        "confirm-ssn-2-digit": "",
        "confirm-ssn-4-digit": "",
        birthDay: "1",
        birthMonth: "1",
        birthYear: new Date().getFullYear().toString(),
        ssnValid: true,
        errorMessage: tLang("dashboard.creditScore.fullSSNComp.errorMessage", { ssnFullText: ssnFullText }),
        dobValid: true,
        notOlderMessage: tLang("dashboard.creditScore.fullSSNComp.notOlderMessage"),
    };

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const isDate18orMoreYearsOld = (day: number, month: number, year: number) => {
        return new Date(year + 18, month - 1, day).getTime() <= new Date().getTime();
    };

    let strSubmitButtonText = "Submit";
    let ssnInputFieldType = "password";

    // use form-submit-hook
    const { register, errors, reset, formState, handleSubmit, clearErrors } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const { isSubmitting } = formState;
    const [isLoading, setIsLoading] = useState(false);
    const [fullSSN, setFullSSN] = useState(initState);

    // in case of dialog, register event for escape press
    window.addEventListener("keyup", (evt: any) => {
        evt = evt || window.event;
        var isEscape = false;
        var isEnter = false;
        if ("key" in evt) {
            isEscape = evt.key === "Escape" || evt.key === "Esc";
        } else {
            isEscape = evt.keyCode === 27;
        }
        if ("key" in evt){
            isEnter = evt.key === "Enter" || evt.key === "Ent";
        } else {
            isEnter = evt.keyCode === 13;
        }
        if (isEscape) {
            window.removeEventListener("keyup", () => {});
            onClose();
        }
        if (isEnter){
            window.removeEventListener("keyup", () => {});
            onClose();
        }
    });

    function onTextBoxChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        onChangeHandler(event);
        let targetName = event.target.name;
        let targetValue = event.target.value;
       
        switch (targetName) {
            case "check-ssn-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("check-ssn-2-digit")?.focus();
                }
                break;
            case "check-ssn-2-digit":
                if (targetValue.length > 1) {
                    document.getElementById("check-ssn-4-digit")?.focus();
                }
                break;
            case "check-ssn-4-digit":
                if (targetValue.length > 3) {
                    document.getElementById("confirm-ssn-3-digit")?.focus();
                }
                break;
            case "confirm-ssn-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("confirm-ssn-2-digit")?.focus();
                }
                break;
            case "confirm-ssn-2-digit":
                if (targetValue.length > 1) {
                    document.getElementById("confirm-ssn-4-digit")?.focus();
                }
                break;
            default:
                break;
        }
    }

    function onChangeHandler(event: any) {
        clearErrors();
        const elName = event.currentTarget.name;
        setFullSSN({ ...fullSSN, [elName as string]: event.currentTarget.value });
    }

    // handle form submit
    const onSubmit = () => {
        const ssn = fullSSN["check-ssn-3-digit"] + fullSSN["check-ssn-2-digit"] + fullSSN["check-ssn-4-digit"];
        const confirmSSN = fullSSN["confirm-ssn-3-digit"] + fullSSN["confirm-ssn-2-digit"] + fullSSN["confirm-ssn-4-digit"];
        if (ssn === confirmSSN) {
            setFullSSN({ ...fullSSN, ssnValid: true });
            let dob = "";
            if (csidUserStatus && csidUserStatus["dob-status"]?.toUpperCase() === "REQUIRED") {
                dob = fullSSN.birthMonth + "/" + fullSSN.birthDay + "/" + fullSSN.birthYear;
                const tempDay = parseInt(fullSSN.birthDay, 10);
                const tempMonth = parseInt(fullSSN.birthMonth, 10);
                const tempYear = parseInt(fullSSN.birthYear, 10);
                const is18YearsOld = isDate18orMoreYearsOld(tempDay, tempMonth, tempYear);
                if (!is18YearsOld) {
                    // show error
                    setFullSSN({ ...fullSSN, dobValid: false });
                    return;
                } else {
                    setFullSSN({ ...fullSSN, dobValid: true });
                }
            }

            setIsLoading(true);
            services
                .saveSsnAndDob(confirmSSN, dob)
                .then((data: any) => {
                    setIsLoading(false);
                    if (data && !data.hasError && data.errors?.length < 1) {
                        getUserCSIDStatus(true);
                        onClose();
                    } else {
                        Notification({
                            type: "error",
                            title: `Error`,
                            message: data.errors[0],
                        });
                    }
                })
                .catch(function (error) {
                    setIsLoading(false);
                    Notification({
                        type: "error",
                        title: `Error`,
                        message: `Something went wrong, Please try again.`,
                    });
                });
        } else {
            setFullSSN({ ...fullSSN, ssnValid: false });
        }
    };

    // handle close button click
    // add reason, so that the callback function can identify, if it's cancel (incase of dialog: ESC press) or button click close
    const onClose = () => {
        // reset form
        reset();
        // execute method passed as props for onCloseButtonClick
        if (props.onCloseCallback != null) {
            props.onCloseCallback();
        }
    };

    return (
        <React.Fragment>
            <div className={`${isLoading && "loading"}`}>
                <form id="form-fullssn" onSubmit={handleSubmit(onSubmit)}>
                    <div className="dialog-header">
                        <div className="dialog-icon status-default">
                            <RenderIcons iconName="user-shield" />
                        </div>
                        <h1>Complete Profile</h1>
                    </div>

                    <div className="dialog-body">
                        <div className="box-description">{/*<h2>Header Here</h2>*/}</div>
                        {csidUserStatus && csidUserStatus["ssn-status"]?.toUpperCase() === "REQUIRED" && (
                            <div className="form-row">
                                <div className="form-input">
                                    <div className="row">
                                        <div className="form-label">
                                            <label className="control-label" htmlFor="check-ssn-3-digit">
                                                {tLang("dashboard.creditScore.fullSSNComp.enterFullSSN", { ssnFullText: ssnFullText })}
                                                <span className="required" title="Mandatory">
                                                    {" "}
                                                    *{" "}
                                                </span>
                                            </label>
                                        </div>
                                        <div className="space-divider margin-top-l"></div>
                                        <div className="form-input row">
                                            <div role="alert" aria-label="Enter your Social Security Number" className="col-xs-4 col-sm-2 col-no-gutter">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                    type={ssnInputFieldType}
                                                    name="check-ssn-3-digit"
                                                    id="check-ssn-3-digit"
                                                    maxLength={3}
                                                    className="inputbox"
                                                    autoComplete="off"
                                                    autoFocus={true}
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Enter your Social Security Number"
                                                />
                                            </div>
                                            <div role="alert" className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(\d{2})(?!-?0{2})(|-\d{2})?$)/ })}
                                                    type={ssnInputFieldType}
                                                    name="check-ssn-2-digit"
                                                    id="check-ssn-2-digit"
                                                    maxLength={2}
                                                    autoComplete="off"
                                                    className="inputbox"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Enter your Social Security Number "
                                                />
                                            </div>
                                            <div role="alert" className="col-xs-4 col-sm-2 col-no-gutter">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(?!0{4})(\d{4})(?!-?0{4})(|-\d{4})?$)/ })}
                                                    type={ssnInputFieldType}
                                                    name="check-ssn-4-digit"
                                                    id="check-ssn-4-digit"
                                                    maxLength={4}
                                                    autoComplete="off"
                                                    className="inputbox"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Enter your Social Security Number  "
                                                />
                                            </div>
                                        </div>
                                        {({ ...errors["check-ssn-3-digit"] }.type === "required" ||
                                            { ...errors["check-ssn-2-digit"] }.type === "required" ||
                                            { ...errors["check-ssn-4-digit"] }.type === "required") && (
                                            <span className="form-error">{tLang("dashboard.creditScore.fullSSNComp.requiredErrorMessage", { ssn: ssn })}</span>
                                        )}

                                        {({ ...errors["check-ssn-3-digit"] }.type === "pattern" ||
                                            { ...errors["check-ssn-2-digit"] }.type === "pattern" ||
                                            { ...errors["check-ssn-4-digit"] }.type === "pattern") && (
                                            <span className="form-error"> {tLang("dashboard.creditScore.fullSSNComp.invalidErrorMessage")}</span>
                                        )}
                                    </div>
                                    <div className="row">
                                        <div className="form-label">
                                            <label className="control-label" htmlFor="confirm-ssn-3-digit">
                                                {tLang("dashboard.creditScore.fullSSNComp.confirmSSN", { ssnFullText: ssnFullText })}
                                                <span className="required" title="Mandatory">
                                                    {" "}
                                                    *{" "}
                                                </span>
                                            </label>
                                        </div>
                                        <div className="space-divider margin-top-l"></div>
                                        <div className="form-input row">
                                            <div role="alert" className="col-xs-4 col-sm-2 col-no-gutter">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                    type={ssnInputFieldType}
                                                    name="confirm-ssn-3-digit"
                                                    id="confirm-ssn-3-digit"
                                                    maxLength={3}
                                                    className="inputbox"
                                                    autoComplete="off"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Confirm your Social Security Number"
                                                />
                                            </div>
                                            <div role="alert" className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(\d{2})(?!-?0{2})(|-\d{2})?$)/ })}
                                                    type={ssnInputFieldType}
                                                    name="confirm-ssn-2-digit"
                                                    id="confirm-ssn-2-digit"
                                                    maxLength={2}
                                                    autoComplete="off"
                                                    className="inputbox"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Confirm your Social Security Number "
                                                />
                                            </div>
                                            <div role="alert" className="col-xs-4 col-sm-2 col-no-gutter">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(?!0{4})(\d{4})(?!-?0{4})(|-\d{4})?$)/ })}
                                                    type={ssnInputFieldType}
                                                    name="confirm-ssn-4-digit"
                                                    id="confirm-ssn-4-digit"
                                                    maxLength={4}
                                                    autoComplete="off"
                                                    className="inputbox"
                                                    onChange={onChangeHandler}
                                                    aria-label="Confirm your Social Security Number  "
                                                />
                                            </div>
                                        </div>
                                        {({ ...errors["confirm-ssn-3-digit"] }.type === "required" ||
                                            { ...errors["confirm-ssn-2-digit"] }.type === "required" ||
                                            { ...errors["confirm-ssn-4-digit"] }.type === "required") && (
                                            <span className="form-error">{tLang("dashboard.creditScore.fullSSNComp.confirmSSNRequired", { ssn: ssn })}</span>
                                        )}

                                        {({ ...errors["confirm-ssn-3-digit"] }.type === "pattern" ||
                                            { ...errors["confirm-ssn-2-digit"] }.type === "pattern" ||
                                            { ...errors["confirm-ssn-4-digit"] }.type === "pattern") && (
                                            <span className="form-error"> {tLang("dashboard.creditScore.fullSSNComp.invalidErrorMessage")}</span>
                                        )}
                                    </div>
                                    {!fullSSN.ssnValid && <p style={{ color: "red" }}>{fullSSN.errorMessage}</p>}
                                </div>
                            </div>
                        )}

                        {csidUserStatus && csidUserStatus["dob-status"]?.toUpperCase() === "REQUIRED" && (
                            <div className="form-row">
                                <div className="form-label">
                                    <label htmlFor="dob">
                                        Date of Birth{" "}
                                        <span className="required" title="Mandatory">
                                            {" "}
                                            *{" "}
                                        </span>{" "}
                                    </label>
                                </div>
                                <div className="form-input row">
                                    <div className="col-xs-4 col-sm-3 col-no-gutter">
                                        <select
                                            ref={register}
                                            name="birthMonth"
                                            className="inputbox"
                                            id="label-dob-month"
                                            onChange={onChangeHandler}
                                            value={fullSSN.birthMonth}
                                        >
                                            {months.map((month, index) => {
                                                return (
                                                    <option key={month} value={index + 1}>
                                                        {month}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="col-xs-4 col-sm-3 col-gutter-horizontal">
                                        <select
                                            ref={register}
                                            name="birthDay"
                                            className="inputbox"
                                            id="label-dob-day"
                                            onChange={onChangeHandler}
                                            value={fullSSN.birthDay}
                                        >
                                            {Array(31)
                                                .fill(null)
                                                .map((value, index) => {
                                                    return (
                                                        <option key={index + 1} value={index + 1}>
                                                            {index + 1}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div className="col-xs-4 col-sm-3 col-no-gutter">
                                        <select
                                            ref={register}
                                            name="birthYear"
                                            className="inputbox"
                                            id="label-dob-year"
                                            onChange={onChangeHandler}
                                            value={fullSSN.birthYear}
                                        >
                                            {Array(115)
                                                .fill(null)
                                                .map((value, index) => {
                                                    const optionValue = new Date().getFullYear() - index;
                                                    return (
                                                        <option key={optionValue} value={optionValue}>
                                                            {optionValue}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                </div>
                                {!fullSSN.dobValid && <p style={{ color: "red" }}>{fullSSN.notOlderMessage}</p>}
                            </div>
                        )}
                    </div>

                    <div className="dialog-footer">
                        <div className="row between-xs">
                            <div className="col-xs col-no-gutter flex-grow-null"></div>
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <button type="submit" className="btn-default btn-primary" disabled={Object.keys(errors).length > 0 || isSubmitting}>
                                    <span className="btn-text">{strSubmitButtonText}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </React.Fragment>
    );
};

export default ProfileCompleteDialog;
